import { Input } from '@components-deprecated'

import { yupResolver } from '@hookform/resolvers/yup'
import { TFunction } from 'i18next'
import React, { ReactElement, useCallback, useMemo, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { Voucher, VoucherInboxState, VoucherUpdatePayloadData } from '@modules-deprecated/inbox/types'

import { FileUploadRefProps } from '../../../../../../components-deprecated/FileUpload'
import { ImageFile, UploadItem } from '../../../../../../components-deprecated/FileUpload/types'
import { useDeleteVoucherMutation } from '../../../../hooks/useDeleteVoucherMutation'
import { useUpdateVoucherMutation } from '../../../../hooks/useUpdateVoucherMutation'
import { useVoucherFileReplaceMutation } from '../../../../hooks/useVoucherFileReplaceMutation'
import { extendVoucherDescription } from '../../../../utils/extendVoucherDescription'
import { MissingInformationActions } from '../../MissingInformationActions'
import { MissingInformationContent } from '../../MissingInformationContent'

type FormInputs = {
  amount: number | string
}

const defaultValues: FormInputs = {
  amount: '',
}

const validationSchema = (t: TFunction) =>
  yup.object().shape({
    amount: yup.number().typeError(t('voucher.inbox.form.amount_error')).required(t('required')),
  })

interface MissingVatProps {
  voucher: Voucher
}

export const MissingVat = ({ voucher }: MissingVatProps): ReactElement => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const fileUploadRef = useRef<FileUploadRefProps>(null)
  const form = useForm<FormInputs>({
    defaultValues,
    resolver: useMemo(() => yupResolver(validationSchema(t)), [t]),
  })

  const currentAmount = form.watch('amount')

  const mutationBaseData = {
    organizationId: organization?.id as string,
    voucherId: voucher.id,
  }

  const deleteMutation = useDeleteVoucherMutation(mutationBaseData)
  const updateMutation = useUpdateVoucherMutation(mutationBaseData)
  const filesReplaceMutation = useVoucherFileReplaceMutation({
    ...mutationBaseData,
    onSuccess: () => {
      const payload = getUpdatePayload(voucher, { amount: currentAmount })
      updateMutation.mutate(payload)
    },
  })

  const isLoading = filesReplaceMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading

  const handleFileUpload = useCallback(
    (_: UploadItem, itemFile?: ImageFile) => {
      if (!itemFile) {
        return
      }

      return filesReplaceMutation.mutateAsync([itemFile])
    },
    [filesReplaceMutation],
  )

  const handleFormSubmit = useCallback(
    (values: FormInputs) => {
      const payload = getUpdatePayload(voucher, values)
      updateMutation.mutate(payload)
    },
    [updateMutation, voucher],
  )

  const handleSubmit = useCallback(() => {
    const isSomeFileDropped = fileUploadRef.current?.isSomeFileDropped()

    if (isSomeFileDropped) {
      fileUploadRef.current?.submitFileUpload()
      return
    }

    form.handleSubmit(handleFormSubmit)()
  }, [form, handleFormSubmit])

  const handleDelete = useCallback(() => {
    deleteMutation.mutate()
  }, [deleteMutation])

  const handleFileDrop = (filesAccepted: File[]) => {
    if (filesAccepted.length > 0) {
      form.clearErrors()
    }
  }

  return (
    <MissingInformationContent
      ref={fileUploadRef}
      disabled={isLoading}
      onFileUpload={handleFileUpload}
      onFileDrop={handleFileDrop}
    >
      <FormProvider {...form}>
        <Input formControl={form.control} label={t('amount')} name="amount" disabled={isLoading} type="number" />
      </FormProvider>

      <MissingInformationActions onSubmit={handleSubmit} onDelete={handleDelete} disabled={isLoading} />
    </MissingInformationContent>
  )
}

function getUpdatePayload(voucher: Voucher, formValues: FormInputs): VoucherUpdatePayloadData {
  const { amount } = formValues
  const description = amount
    ? extendVoucherDescription(voucher.description, { vatAmount: amount })
    : voucher.description

  return { description, inboxState: VoucherInboxState.RECEIVED }
}
